// src/data/boardStage.js
//
// THE ARBITRATION STAGE AS A DERIVED OVERLAY - platform layer §5.2. Ruling under PD-19, withdrawable
// by number.
//
// §5.2 of data\YCP_ARBITRATION_LAYER_2026-08-27.md: "An arbitration STAGE on both lifecycles. Claim:
// resolved -> progressed_to_board. Discipline: resolved -> progressed_to_board. Without it the
// platform records a live case as finished."
//
// WHY AN OVERLAY AND NOT A NEW RUNG. The claim lifecycle ends at `closure` (src\data\claimLadder.js
// LADDER) with `archived` off-ladder; the discipline lifecycle ends at `resolved`
// (src\pages\DisciplineCases.js). Adding a `progressed_to_board` rung INTO either ladder would change
// the core lifecycle a member and officer act on, and the off-property axis is already a SEPARATE axis
// by design - appealLadder.js's own header: "Elevation moves a claim between offices. PRESENTMENT
// MOVES IT OFF THE PROPERTY... this module is a SECOND AXIS and not more rungs." So the Board stage is
// derived from that second axis, exactly as appealLadder.standing() already is, and read beside the
// on-property status - never welded into it.
//
// THE DEFECT THIS EXISTS TO PREVENT, stated as the check: a matter whose on-property status reads as
// FINISHED (closure/archived for a claim, resolved for a discipline case) while a Board case on it is
// still LIVE. That matter is not finished; it has left the property. misreportedAsFinished names
// exactly that, so a list or a status bar can refuse to render "done" over a live arbitration.
//
// SOURCES, ONE EACH (§XI): where the matter stands off-property is appealLadder.standing() - not
// recomputed here; whether a linked Board case is live is arbitrationCases.isLive() - not a second
// liveness test. This module only COMBINES them with the on-property status.
//
// Pure module: no React, no Firestore. Proved by scripts\test-board-stage.mjs.

import { standing as appealStanding, lastOf, STANDING } from './appealLadder';
import { isLive } from './arbitrationCases';

const F = Object.freeze;

// The derived stage key. It is NOT a member of either ladder's status set on purpose - a reader that
// sees it knows to look at the off-property axis, not to search claimLadder for it.
export const PROGRESSED_TO_BOARD = 'progressed_to_board';

// The on-property "finished" states, read from the two lifecycles as they actually stand (VF-2), not
// invented. A caller passes the set for the matter's kind; boardStageClosedStates() gives the right one.
export const CLAIM_CLOSED_STATES = F(['closure', 'archived']);        // claimLadder LADDER end + OFF_LADDER
export const DISCIPLINE_CLOSED_STATES = F(['resolved']);             // DisciplineCases status list terminal

export function boardStageClosedStates(kind) {
  return String(kind) === 'discipline' ? DISCIPLINE_CLOSED_STATES : CLAIM_CLOSED_STATES;
}

// The appeal-ladder standings that mean the matter is AT or BEYOND a Board. AWARDED is included but is
// only "open" until the award is complied with (below).
const BOARD_STANDINGS = F([STANDING.AT_BOARD, STANDING.AWARDED, STANDING.COMPLIANCE_OPEN]);

// Is the matter OPEN before a Board, judged by the appeal-ladder acts alone?
//   - AT_BOARD (notice/submission filed) and COMPLIANCE_OPEN (award not complied with) are open.
//   - AWARDED is open UNTIL an award_complied act closes it - a complied award is the genuine end of
//     the matter, and after it the on-property "finished" reading is correct, not a defect.
export function isBeforeBoardByActs(appealActs) {
  const st = appealStanding(appealActs);
  if (BOARD_STANDINGS.indexOf(st.key) === -1) return false;
  if (st.key === STANDING.AWARDED && lastOf(appealActs, 'award_complied')) return false;
  return true;
}

// The whole picture for one matter's lifecycle bar. Reads three things and computes one honest answer.
//
//   status          - the on-property status (claim: e.g. 'closure'; discipline: e.g. 'resolved')
//   kind            - 'claim' | 'discipline', selects the closed-state set
//   opts.appealActs - the appeal-ladder acts on this matter (the off-property axis)
//   opts.linkedBoardCases - Stage-1 arbitration cases linked to this matter
//
// A LIVE linked Board case is treated as an open Board matter. That is the SAFE direction on purpose:
// the failure this stage prevents is a live matter shown finished, so where the two signals could
// disagree we surface the matter rather than hide it. A Board case that has genuinely concluded is
// marked so on its own record (its record_class leaves LIVE, or an award_complied act is recorded),
// and both of those flip this back to finished.
export function lifecycleStanding(status, kind, opts) {
  const o = opts || {};
  const appealActs = Array.isArray(o.appealActs) ? o.appealActs : [];
  const liveLinked = (Array.isArray(o.linkedBoardCases) ? o.linkedBoardCases : []).filter(isLive);
  const closedStates = boardStageClosedStates(kind);

  const beforeBoard = isBeforeBoardByActs(appealActs) || liveLinked.length > 0;
  const finishedOnProperty = closedStates.indexOf(String(status || '')) !== -1;
  const misreportedAsFinished = finishedOnProperty && beforeBoard;

  const appeal = appealStanding(appealActs);
  const stage = beforeBoard ? PROGRESSED_TO_BOARD : String(status || '');
  const label = beforeBoard
    ? 'Progressed to a Board - ' + appeal.label + ' (not finished on the property)'
    : (finishedOnProperty ? 'Finished on the property (' + String(status || '') + ')' : String(status || ''));

  return {
    onProperty: String(status || ''),
    kind: String(kind || 'claim'),
    beforeBoard,
    finishedOnProperty,
    stage,
    appealStanding: appeal,
    linkedLiveBoardCases: liveLinked.length,
    // The honesty flag. When true, a caller MUST NOT render this matter as done.
    misreportedAsFinished,
    label,
  };
}

export default {
  PROGRESSED_TO_BOARD, CLAIM_CLOSED_STATES, DISCIPLINE_CLOSED_STATES,
  boardStageClosedStates, isBeforeBoardByActs, lifecycleStanding,
};
